import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Link,
  Modal,
  PrimaryButton,
} from "@certn/cascadia-react";
import { type TFunction } from "i18next";
import { Trans, useTranslation } from "react-i18next";

type ConsentDialogContent = {
  header: string | React.ReactNode;
  content: string | React.ReactNode;
};

type ConsentRequiredModalProps = {
  show: boolean;
  onClose: () => void;
  alternativeDialogContent?: ConsentDialogContent;
};

export function buildSignedConsentDialogContent(
  formName: string,
  t: TFunction,
  options?: { isQuickscreen?: boolean }
): ConsentDialogContent {
  return {
    header: t("Signed consent required"),
    content: (
      <>
        <Trans values={{ formName }} components={{ bold: <strong /> }}>
          {
            "You can't continue yet. A signed <bold>{{formName}}</bold> must be uploaded and you must check the box to confirm before you can proceed."
          }
        </Trans>
        <br />
        <br />
        {options?.isQuickscreen ? (
          <Trans>
            Please upload a completed and signed consent form. You may use the
            applicant's own signed version, or download the template using the
            link on this page.
          </Trans>
        ) : (
          <Trans>
            Please upload a completed and signed consent form. You may use your
            own signed version, or download the template using the link on this
            page.
          </Trans>
        )}
      </>
    ),
  };
}

export function ConsentRequiredModal(props: ConsentRequiredModalProps) {
  const { show, onClose, alternativeDialogContent } = props;

  const { t } = useTranslation();

  return (
    <Modal isOpen={show}>
      <Dialog>
        <DialogHeader onClose={onClose} testID="consent-required-modal">
          {alternativeDialogContent
            ? alternativeDialogContent.header
            : t("Consent required")}
        </DialogHeader>

        <DialogContent testID="consent-required-modal">
          {alternativeDialogContent ? (
            alternativeDialogContent.content
          ) : (
            <>
              <Trans>
                Consent to run a check is required in order to move forward.
              </Trans>
              <br />
              <br />
              <Trans>
                If you do not consent, you may close this window and exit your
                application. For further assistance please reach out to Certn
                Support at{" "}
                <Link
                  testID="consent-required"
                  linkStyle="monochrome"
                  href="mailto:support@certn.co"
                >
                  support@certn.co
                </Link>
                .
              </Trans>
            </>
          )}
        </DialogContent>

        <DialogFooter>
          <PrimaryButton
            type="submit"
            size="medium"
            onPress={onClose}
            testID="consent-required-modal-go-back-button"
          >
            <Trans>Go back</Trans>
          </PrimaryButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
